import { submitPreviewSettings } from '@/app/apis/Providers/requests'
import { parseRequestError } from '@/common/utilities'
import { ProviderQueryKeys } from '@/queries/providers/queries'
import { useToast } from '@/shad/ui/toast/use-toast'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'

interface UseSubmitStoreOptions {
  providerId: string
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export const useSubmitPreviewSettings = ({
  providerId,
  onSuccess,
  onError,
}: UseSubmitStoreOptions) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const programId = useSelector(selectSelectedProgram).id

  return useMutation({
    mutationFn: async (programId: number) => {
      return await submitPreviewSettings(providerId, programId)
    },
    onSuccess: () => {
      toast({
        title: 'New Store Version Published',
        description:
          'A new version of your store has been successfully published and is now live.',
        variant: 'default',
      })
      queryClient.invalidateQueries({
        queryKey: ProviderQueryKeys.previewSettings(providerId, programId),
      })
      queryClient.invalidateQueries({
        queryKey: ProviderQueryKeys.details(programId, providerId),
      })

      onSuccess?.()
    },
    onError: (error: Error) => {
      const errorMessage = parseRequestError(error).message

      toast({
        title: 'Publishing Store Failed',
        description: errorMessage,
        variant: 'destructive',
      })

      onError?.(error)
    },
  })
}
